import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import toastr from "../../../utils/toast";
import { openInAppTab } from "../../../utils/tabNavigation";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// The best ones first; the rest behind "Show all".
const FIRST_SHOWN = 15;

// A profile last refreshed longer ago than this is shown as out of date.
const SIX_MONTHS_MS = 183 * 24 * 60 * 60 * 1000;

// Some PINE titles were saved with HTML entities in them ("North &amp; South").
const plain = (text) =>
  String(text || "")
    .replace(/&amp;/g, "&")
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

const FIT_STYLE = {
  strong: { background: "hsl(142 71% 45% / 0.15)", color: "#15803D" },
  good: { background: "#F1F3F5", color: "#495057" },
};

/**
 * "Who do we already know there?" - the people PINE holds at one company who
 * fit this role, best first, each a tick away from being added to it.
 *
 * Picking is done by the backend: word matching on job titles, then the model
 * reads those titles. Adding is PINE's own add_to_role, one person at a time,
 * so every add writes the normal role link and logs.
 *
 * @param {object}   props
 * @param {number}   props.roleId
 * @param {object}   props.company       { name, mapCompanyId? } - mapCompanyId
 *                                       for a company on the map, else the name.
 *                                       Opened from a suggestion it also carries
 *                                       suggestionId (a company suggestion) or
 *                                       industrySuggestionId + industryName +
 *                                       pineIndustry (a suggested new industry):
 *                                       adding someone then puts that company /
 *                                       industry on the map too.
 * @param {string}   props.token
 * @param {Function} props.onClose
 * @param {Function} props.onAdded       called after people were added
 */
export default function WhoWeKnow({ roleId, company, token, onClose, onAdded }) {
  // "now" - work there now; "past" - worked there before. Each tab loads the
  // first time it is opened; "past" reads the whole work history, a few seconds.
  const [tab, setTab] = useState("now");
  const [results, setResults] = useState({});
  const [errors, setErrors] = useState({});
  const requested = useRef(new Set());
  const [ticked, setTicked] = useState(new Set());
  const [showAll, setShowAll] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(
    async (which) => {
      setErrors((prev) => ({ ...prev, [which]: "" }));
      try {
        const res = await axios.post(
          `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${roleId}/people`,
          {
            ...(company.mapCompanyId
              ? { map_company_id: company.mapCompanyId }
              : { name: company.name }),
            past: which === "past",
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setResults((prev) => ({ ...prev, [which]: res.data }));
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          [which]:
            err.response?.data?.error || "Could not find the people at this company.",
        }));
      }
    },
    [roleId, company.mapCompanyId, company.name, token],
  );

  useEffect(() => {
    if (requested.current.has(tab)) return;
    requested.current.add(tab);
    load(tab);
  }, [tab, load]);

  const data = results[tab] || null;
  const error = errors[tab] || "";

  const switchTab = (next) => {
    setTab(next);
    setTicked(new Set());
    setShowAll(false);
  };

  const toggle = (id) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Can adding someone also put their company / industry on the map? If so
  // the consultant is asked first (askAboutMap).
  const canMap =
    !!company.suggestionId || (!!company.industrySuggestionId && !!company.pineIndustry);

  // An industry with no PINE fit cannot go on the map - said up front, since
  // there is nothing to ask.
  const mapNote =
    company.industrySuggestionId && !company.pineIndustry
      ? `"${company.industryName}" has no PINE industry yet, so it cannot go on the Search Map - people are added to the role only.`
      : null;

  /**
   * Yes / Cancel before the map changes: "map" or "cancel". There is no "role
   * only": the map holds every company the role's candidates come from, so a
   * person is never added without their company.
   */
  const askAboutMap = async (count) => {
    if (!canMap) return "role";
    const who = count === 1 ? "this person" : `these ${count} people`;
    const result = await Swal.fire({
      title: company.suggestionId
        ? `Add ${company.name} to the Search Map too?`
        : `Add the industry "${company.industryName}" to the Search Map too?`,
      text: company.suggestionId
        ? `Adding ${who} from ${company.name} will also add ${company.name} to its industry's column on the Search Map.`
        : `Adding ${who} from ${company.name} will also add the industry "${company.industryName}" (${company.pineIndustry} in PINE) to the Search Map, with its suggested companies.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#C2570C",
    });
    return result.isConfirmed ? "map" : "cancel";
  };

  // A company or industry the person's company came from, put on the map the
  // way its own Add button does. Returns the toast line, or null.
  const addCompanyToMap = async () => {
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const base = `${REACT_APP_API_BASE_URL}/workstation/gap_analysis/${roleId}`;
    try {
      if (company.suggestionId) {
        const res = await axios.post(`${base}/suggestion/${company.suggestionId}/add`, {}, auth);
        return res.data?.already_on_map ? null : `${company.name} added to the Search Map.`;
      }
      if (company.industrySuggestionId && company.pineIndustry) {
        const res = await axios.post(
          `${base}/industry_suggestion/${company.industrySuggestionId}/add`,
          {},
          auth,
        );
        return res.data?.already_added
          ? null
          : `${res.data?.industry || company.pineIndustry} added to the Search Map.`;
      }
    } catch (err) {
      toastr.error(
        err.response?.data?.error || `Could not put ${company.name} on the Search Map.`,
      );
    }
    return null;
  };

  // One person at a time through PINE's own add, so each gets the normal logs.
  const addToRole = async (ids) => {
    if (!ids.length) return;
    const choice = await askAboutMap(ids.length);
    if (choice === "cancel") return;
    setAdding(true);
    let added = 0;
    let failed = 0;
    for (const candidate_id of ids) {
      try {
        await axios.post(
          `${REACT_APP_API_BASE_URL}/candidates/add_to_role`,
          { role_id: roleId, candidate_id },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        added += 1;
      } catch (err) {
        failed += 1;
      }
    }
    const mapLine = added && choice === "map" ? await addCompanyToMap() : null;
    setAdding(false);
    setTicked(new Set());
    if (added) {
      toastr.success(
        `${added} added to the role.${mapLine ? ` ${mapLine}` : ""}`,
      );
      onAdded();
    }
    if (failed) toastr.error(`${failed} could not be added. Try again.`);
    // This tab again now; the other one afresh when it is next opened.
    requested.current = new Set([tab]);
    setResults((prev) => ({ [tab]: prev[tab] }));
    await load(tab);
  };

  const fits = data?.fits || [];
  const shown = showAll ? fits : fits.slice(0, FIRST_SHOWN);
  const onRole = data?.on_role || [];

  // LinkedIn as the workstation grid opens it: a stored link without a scheme
  // gets one, so it does not open as a path inside PINE.
  const linkedinUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

  const nameLink = (p) => (
    <>
      <a
        href={`/candidates/view/${p.id}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.preventDefault();
          openInAppTab(`/candidates/view/${p.id}`);
        }}
        className="fw-semibold"
      >
        {p.name || `Candidate ${p.id}`}
      </a>
      {p.linkedin && (
        <a
          href={linkedinUrl(p.linkedin)}
          target="_blank"
          rel="noopener noreferrer"
          title="LinkedIn profile"
          aria-label={`${p.name || "Candidate"} on LinkedIn`}
          onClick={(event) => event.stopPropagation()}
        >
          <i className="fa-brands fa-linkedin"></i>
        </a>
      )}
    </>
  );

  // When the profile was last refreshed, bottom right of each box: grey when
  // recent, orange when over 6 months old or never.
  const lastUpdated = (p) => {
    const stale = !p.last_updated || Date.now() - p.last_updated > SIX_MONTHS_MS;
    return (
      <span
        className="text-nowrap"
        style={{ fontSize: 11, color: stale ? "#C2570C" : "#6C757D" }}
      >
        {p.last_updated
          ? `Last updated ${new Date(p.last_updated).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}${stale ? " · over 6 months" : ""}`
          : "Never updated"}
      </span>
    );
  };

  // A person's job now; in the past tab also where, and what they did at the
  // company and when.
  const jobLines = (p) => (
    <>
      <span className="d-block small">
        {plain(p.title)}
        {p.now_at && <span className="text-muted"> · now at {plain(p.now_at)}</span>}
      </span>
      {p.was && (
        <span className="d-block text-muted" style={{ fontSize: 12 }}>
          At {data?.company || company.name}: {plain(p.was.title) || "role not recorded"}
          {p.was.from &&
            (p.was.to ? ` · ${p.was.from}–${p.was.to}` : ` · from ${p.was.from}`)}
        </span>
      )}
    </>
  );

  const fitBadge = (p) =>
    p.fit && (
      <span
        className="rounded-pill px-2 text-nowrap"
        style={{ fontSize: 11, ...FIT_STYLE[p.fit] }}
      >
        {p.fit === "strong" ? "Strong fit" : "Good fit"}
      </span>
    );

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h4 className="modal-title">
                  {company.name} — people in PINE who fit this role
                </h4>
                {data && (
                  <p className="small fw-semibold mb-0" style={{ color: "#C2570C" }}>
                    {fits.length} who fit, out of {data.total}{" "}
                    {data.total === 1 ? "person" : "people"}{" "}
                    {tab === "past"
                      ? `in PINE who used to work at ${data.company}`
                      : `at ${data.company} in PINE`}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>

            <div className="modal-body">
              <ul className="nav nav-tabs mb-3">
                {[
                  ["now", "Works there now"],
                  ["past", "Worked there before"],
                ].map(([key, label]) => (
                  <li key={key} className="nav-item">
                    <button
                      type="button"
                      className={`nav-link${tab === key ? " active" : ""}`}
                      disabled={adding}
                      onClick={() => switchTab(key)}
                    >
                      {label}
                      {results[key] ? ` (${results[key].total})` : ""}
                    </button>
                  </li>
                ))}
              </ul>

              {!data && !error && (
                <div className="d-flex align-items-center gap-2 text-muted">
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  {tab === "past"
                    ? `Looking through work histories for people who used to work at ${company.name}… this takes a few seconds.`
                    : `Reading the job titles at ${company.name}…`}
                </div>
              )}

              {error && <div className="alert alert-danger mb-0">{error}</div>}

              {data && mapNote && (
                <div className="alert alert-light border small py-2 mb-3">
                  <i className="fa-solid fa-sitemap me-1 text-muted"></i>
                  {mapNote}
                </div>
              )}

              {data && fits.length === 0 && (
                <p className="text-muted mb-3">
                  {tab === "past"
                    ? `Nobody in PINE who used to work at ${data.company} fits this role by their current job title.`
                    : `Nobody at ${data.company} in PINE fits this role by job title.`}
                </p>
              )}

              {fits.length > 0 && (
                <>
                  <div className="text-muted text-uppercase fw-semibold small mb-2">
                    Who fits
                  </div>
                  <div className="d-flex flex-column gap-1 mb-2">
                    {shown.map((p) => (
                      <label
                        key={p.id}
                        className="d-flex align-items-start gap-2 border rounded px-2 py-2 mb-0"
                        style={{ cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          className="form-check-input mt-1"
                          checked={ticked.has(p.id)}
                          onChange={() => toggle(p.id)}
                          disabled={adding}
                        />
                        <span className="flex-grow-1" style={{ minWidth: 0 }}>
                          <span className="d-flex align-items-center flex-wrap gap-2">
                            {nameLink(p)}
                            {fitBadge(p)}
                          </span>
                          {jobLines(p)}
                          {/* Spans, not a list: the row is a <label>, which
                              may only hold inline content. */}
                          {p.points.map((point) => (
                            <span
                              key={point}
                              className="d-flex gap-1 text-muted"
                              style={{ fontSize: 12 }}
                            >
                              <span aria-hidden="true">•</span>
                              <span>{point}</span>
                            </span>
                          ))}
                        </span>
                        <span className="d-flex flex-column align-items-end justify-content-between align-self-stretch gap-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary text-nowrap"
                            disabled={adding}
                            onClick={(event) => {
                              event.preventDefault();
                              addToRole([p.id]);
                            }}
                          >
                            <i className="fa-solid fa-plus me-1"></i>Add
                          </button>
                          {lastUpdated(p)}
                        </span>
                      </label>
                    ))}
                  </div>
                  {fits.length > FIRST_SHOWN && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 mb-3"
                      onClick={() => setShowAll((v) => !v)}
                    >
                      {showAll ? "Show the best 15" : `Show all ${fits.length}`}
                    </button>
                  )}
                </>
              )}

              {onRole.length > 0 && (
                <>
                  <div className="text-muted text-uppercase fw-semibold small mt-2 mb-2">
                    Already on this role ({onRole.length})
                  </div>
                  <div className="d-flex flex-column gap-1">
                    {onRole.map((p) => (
                      <div
                        key={p.id}
                        className="d-flex align-items-start gap-2 rounded px-2 py-2"
                        style={{ background: "#F8F9FA" }}
                      >
                        <span className="flex-grow-1 text-muted" style={{ minWidth: 0 }}>
                          <span className="d-flex align-items-center flex-wrap gap-2">
                            {nameLink(p)}
                            {fitBadge(p)}
                          </span>
                          {jobLines(p)}
                        </span>
                        <span className="d-flex flex-column align-items-end justify-content-between align-self-stretch gap-2">
                          <span className="small text-muted text-nowrap">On this role</span>
                          {lastUpdated(p)}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={onClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary d-inline-flex align-items-center"
                disabled={!ticked.size || adding}
                onClick={() => addToRole([...ticked])}
              >
                <i className="fa-solid fa-user-plus me-1"></i>
                {adding
                  ? "Adding…"
                  : `Add ${ticked.size || ""} to role`.replace("  ", " ")}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}
